/**
 * useSsoSignIn — ADR-0330 Phase B (R-2 / R-3).
 *
 * Tries the popup-bridge first. If the browser blocks the popup,
 * keep listening for postMessage and show a same-origin "Open sign-in"
 * control (user gesture). OAuth redirect is ONLY via continueWithOAuth
 * (or when the bridge is kill-switched) — never auto after a bridge miss (S+1).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabase } from './supabase.ts';
import {
  JubitSsoBridgeError,
  openJubitSsoBridge,
  shouldUseSsoBridge,
  waitForSsoBridgeResult,
  type SsoBridgeAttempt,
} from './jubitSsoBridge.ts';
import { useUserStore } from '../state/userStore.ts';

export type SsoSignInPhase = 'idle' | 'opening' | 'blocked';

export function useSsoSignIn(): {
  readonly phase: SsoSignInPhase;
  readonly fallbackHref: string | null;
  readonly start: (provider?: 'google' | 'github' | 'apple') => Promise<void>;
  readonly continueWithOAuth: (provider?: 'google' | 'github' | 'apple') => Promise<void>;
} {
  const signIn = useUserStore((s) => s.signIn);
  const refresh = useUserStore((s) => s.refresh);
  const [phase, setPhase] = useState<SsoSignInPhase>('idle');
  const [fallbackHref, setFallbackHref] = useState<string | null>(null);
  const waitAbort = useRef(false);

  useEffect(() => {
    return () => {
      waitAbort.current = true;
    };
  }, []);

  const applyTokens = useCallback(
    async (accessToken: string, refreshToken: string) => {
      const supabase = getSupabase();
      if (!supabase) return;
      // S3: clear sticky local session before replace (same as userStore.signIn).
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        /* best-effort */
      }
      // S2: mirror jubitSsoExchange — check setSession `{ error }`; never
      // fall through silently to Google OAuth on a failed handoff.
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) {
        throw new JubitSsoBridgeError(
          'set-session-failed',
          error.message || 'Failed to apply SSO session',
        );
      }
      await refresh();
    },
    [refresh],
  );

  const start = useCallback(
    async (provider: 'google' | 'github' | 'apple' = 'google') => {
      if (phase === 'opening') return;
      setPhase('opening');
      setFallbackHref(null);

      if (shouldUseSsoBridge()) {
        try {
          const tokens = await openJubitSsoBridge();
          if (waitAbort.current) return;
          await applyTokens(tokens.accessToken, tokens.refreshToken);
          setPhase('idle');
          return;
        } catch (err) {
          if (waitAbort.current) return;
          if (err instanceof JubitSsoBridgeError && err.code === 'cancelled') {
            setPhase('idle');
            return;
          }
          if (err instanceof JubitSsoBridgeError && err.code === 'popup-blocked') {
            const details = err.details;
            const href = details?.bridgeUrl ?? null;
            setFallbackHref(href);
            setPhase('blocked');
            if (details?.state && details.jubitOrigin) {
              const attempt: SsoBridgeAttempt = {
                url: details.bridgeUrl ?? '',
                state: details.state,
                jubitOrigin: details.jubitOrigin,
              };
              void waitForSsoBridgeResult({
                state: attempt.state,
                jubitOrigin: attempt.jubitOrigin,
              })
                .then(async (tokens) => {
                  if (waitAbort.current) return;
                  await applyTokens(tokens.accessToken, tokens.refreshToken);
                  setPhase('idle');
                  setFallbackHref(null);
                })
                .catch((waitErr) => {
                  // Keep blocked UI on transport miss so user can continueWithOAuth;
                  // setSession fail → idle (no pretend success, no auto-OAuth).
                  if (
                    waitErr instanceof JubitSsoBridgeError &&
                    waitErr.code === 'set-session-failed'
                  ) {
                    if (!waitAbort.current) {
                      setPhase('idle');
                      setFallbackHref(null);
                    }
                    return;
                  }
                  /* timeout / other — keep blocked UI for explicit OAuth */
                });
            }
            return;
          }
          // S+1: ANY other bridge / setSession failure — surface, no auto-OAuth.
          // OAuth only via continueWithOAuth ("Continue with Google instead").
          // S+4.B: surface safe shape breadcrumbs only (never token values).
          if (
            err instanceof JubitSsoBridgeError &&
            err.code === 'malformed-payload' &&
            err.details
          ) {
            console.warn('[sso-bridge]', err.code, {
              atOk: err.details.atOk,
              atSeg: err.details.atSeg,
              rtType: err.details.rtType,
              rtLen: err.details.rtLen,
            });
          }
          setPhase('idle');
          throw err;
        }
      }

      // Bridge disabled / kill-switched only (S+1: never reached after a bridge attempt):
      await signIn(provider, { skipSso: true });
      if (!waitAbort.current) setPhase('idle');
    },
    [applyTokens, phase, signIn],
  );

  const continueWithOAuth = useCallback(
    async (provider: 'google' | 'github' | 'apple' = 'google') => {
      await signIn(provider, { skipSso: true });
      setPhase('idle');
      setFallbackHref(null);
    },
    [signIn],
  );

  return { phase, fallbackHref, start, continueWithOAuth };
}
